import {
  Village,
  VillageDeal,
  VillageMedia,
  VillagePricePeriod,
  VillagePriceVariants,
  VillageSurrounding,
} from '@/services/types';
import { VillaCoverMedia } from '@/types/Villa';
import { format, isAfter, isBefore, isValid, parseISO } from 'date-fns';
import { pugliaMap, sicilyMap, villagesMap } from './maps';

export function getVillageCoverMedia(
  media: VillageMedia[],
): VillaCoverMedia | undefined {
  const cover = media.find((item) => item.type === 'cover') ?? media[0];

  if (!cover) {
    return undefined;
  }

  return {
    id: cover.id,
    path: cover.path,
    filename: cover.filename,
    mediaFilters: cover.mediaFilters,
  };
}

export function getVillageSlug(villageTitle: string) {
  return villageTitle
    .split("'")
    .join('')
    .split(' ')
    .join('-')
    .toLocaleLowerCase();
}

export function getVillageLink({
  lang,
  slug,
  region,
  filterGuests,
  filterDates,
}: {
  lang: string;
  slug: string;
  region: string;
  filterGuests?: {
    adults: number;
    children: number;
    infants: number;
  };
  filterDates?: {
    start: Date | null;
    end: Date | null;
  };
}) {
  const regionLabel = region == 'sicily' ? sicilyMap[lang] : pugliaMap[lang];
  let url = `/${regionLabel}/${villagesMap[lang]}/${slug}`;

  if (lang !== 'en') {
    url = `/${lang}/${regionLabel}/${villagesMap[lang]}/${slug}`;
  }

  const datesParams = new URLSearchParams();

  if (filterDates) {
    const { start, end } = filterDates;

    // Dates come from URL params and may be invalid; skip them instead of throwing
    if (start && end && isValid(start) && isValid(end)) {
      datesParams.append('start', format(start, 'yyyy-MM-dd'));
      datesParams.append('end', format(end, 'yyyy-MM-dd'));
    }

    if (datesParams.toString().length > 0) {
      url = `${url}?${datesParams.toString()}`;
    }
  }

  if (filterGuests) {
    let guestsParams = new URLSearchParams();
    if (filterGuests.adults > 0) {
      guestsParams.append('adults', filterGuests.adults.toString());
    }
    if (filterGuests.infants > 0) {
      guestsParams.append('infants', filterGuests.infants.toString());
    }
    if (filterGuests.children > 0) {
      guestsParams.append('children', filterGuests.children.toString());
    }
    if (guestsParams.toString().length > 0) {
      if (datesParams.toString().length > 0) {
        url = `${url}&${guestsParams.toString()}`;
      } else {
        url = `${url}?${guestsParams.toString()}`;
      }
    }
  }

  return url;
}

export function countRangeDays(rangeValue: {
  start: Date | null;
  end: Date | null;
}) {
  if (rangeValue?.start && rangeValue?.end) {
    const oneDay = 24 * 60 * 60 * 1000;
    const diffInMilliseconds = Math.abs(
      rangeValue.end.getTime() - rangeValue.start.getTime(),
    );
    const diffInDays = Math.round(diffInMilliseconds / oneDay);
    return diffInDays;
  }
}

const NUMBER_LOCALE_BY_LANG: { [key: string]: string } = {
  it: 'it-IT',
  de: 'de-DE',
  fr: 'fr-FR',
  en: 'en-US',
};

// Thousands separator follows the site language, not the selected currency.
export function formatLocalizedNumber(value: number, lang: string) {
  const locale = NUMBER_LOCALE_BY_LANG[lang] ?? NUMBER_LOCALE_BY_LANG.en;

  // it-IT defaults to minimumGroupingDigits 2, so 4-digit numbers come out
  // ungrouped ("2000"). Force grouping from the first thousand ("2.000").
  // minimumGroupingDigits is Intl.NumberFormat v3, not in the TS lib yet.
  const options = {
    useGrouping: true,
    minimumGroupingDigits: 1,
  } as Intl.NumberFormatOptions;

  const formatted = new Intl.NumberFormat(locale, options).format(value);

  // fr-FR emits a narrow no-break space (U+202F); normalize to a regular
  // no-break space (U+00A0) for wider font/browser support.
  return formatted.replace(/\u202F/g, '\u00A0');
}

export function formatVillagePrice(price: number, lang: string) {
  const symbol = lang === 'en' ? '$' : '€';

  return `${symbol} ${formatLocalizedNumber(price, lang)}`;
}

export function getSicilyAirportName(lang: string, latitude: number) {
  if (latitude < 13) {
    return 'Trapani';
  }

  if (latitude < 14 && latitude > 12) {
    if (lang == 'fr') {
      return 'Palerme';
    } else {
      return 'Palermo';
    }
  }

  if (latitude < 15 && latitude > 13) {
    return 'Comiso';
  }

  if (latitude < 16 && latitude > 14) {
    if (lang == 'fr') {
      return 'Catane';
    } else {
      return 'Catania';
    }
  }
  return '';
}

export function findVillagePriceVariant(
  priceVariants: VillagePriceVariants[],
  datesRange: { start: Date | null; end: Date | null },
) {
  let targetVariant: VillagePriceVariants | undefined;
  let variantPeriod: VillagePricePeriod | undefined;
  const currentYear = new Date().getFullYear();
  const currentPriceVariants = priceVariants.filter(
    (variant) => variant.villaPricing.year === currentYear,
  );

  currentPriceVariants.forEach((variant) => {
    const findedPeriod = variant.villaPricing.pricePeriods.find((period) => {
      const startPeriodTime = new Date(period.startDate).getTime();
      const endPeriodTime = new Date(period.endDate).getTime();
      if (datesRange.start && datesRange.end) {
        return (
          datesRange.start.getTime() >= startPeriodTime &&
          datesRange.end.getTime() <= endPeriodTime
        );
      }
    });
    if (findedPeriod) {
      variantPeriod = findedPeriod;
      targetVariant = priceVariants.filter((variant) =>
        variant.villaPricing.pricePeriods.includes(findedPeriod),
      )[0];
    }
  });

  return {
    variantPeriod,
    targetVariant,
  };
}

export function getVillageWishListInfos(village: Village, currency: string) {
  const villageMainFeatures = village.features?.filter(({ feature }) => {
    return feature?.type ? feature.type === 'main_feature' : false;
  });

  const coverMedia = getVillageCoverMedia(village.media);
  const villageMedia = coverMedia?.mediaFilters?.find(
    (img) => img.name === 'medium_3-2',
  )?.url;

  const wishListInfos = {
    id: village.id,
    title: village.title,
    sleeps: village.sleeps,
    bedrooms: village.bedrooms,
    bathrooms: village.bathrooms,
    city: village.city,
    media: villageMedia ? villageMedia : '',
    coverMedia,
    prices: {
      originalPrice: village.prices[0]?.originalPrice,
      // Never read back; the other cards store the public price here too
      ownerPrice: village.prices[0]?.originalPrice,
      currency: currency,
    },
    zone: {
      code: village.zone.code,
      enabled: true,
    },
    villageMainFeatures: villageMainFeatures,
    slug: village.slug,
  };

  return wishListInfos;
}

function isVillageDealVisible(deal: VillageDeal) {
  const { visibleFrom, visibleTo } = deal.deal;
  const now = new Date();

  const onRange =
    Boolean(visibleFrom && visibleTo) &&
    !isBefore(now, parseISO(visibleFrom)) &&
    !isAfter(now, parseISO(visibleTo));
  const isEnabled = deal.enabled && deal.deal.publicVisible;

  return onRange && isEnabled;
}

export function villageHasVisiblePromotion(deals: VillageDeal[]) {
  if (deals && deals.length > 0) {
    return deals.some(isVillageDealVisible);
  }

  return false;
}

export function getVillageVisiblePromotions(
  deals: VillageDeal[],
  limit = 2,
): VillageDeal[] {
  if (!deals || deals.length === 0) {
    return [];
  }

  return deals
    .filter(isVillageDealVisible)
    .sort(
      (a, b) =>
        parseISO(a.deal.visibleTo).getTime() -
        parseISO(b.deal.visibleTo).getTime(),
    )
    .slice(0, limit);
}

// Media filter names read by client components; the API sends ~29 per photo
const CLIENT_MEDIA_FILTER_NAMES = new Set([
  'medium_3-2',
  'large_3-2',
  'large_3-2_webp',
  'small_3-2',
  'small_1-1',
  'medium_2-1',
  'large_gallery_wm',
  'large_gallery_wm_2500',
  'large_gallery_wm_1280_webp',
  'villa_card_2x',
  'villa_card_2x_webp',
  '480w_webp',
  '767w_webp',
  '1280w_webp',
  '1920w_webp',
  '1920w',
  '2400w',
]);

function withMediaFilters(
  media: VillageMedia,
  names: Set<string>,
): VillageMedia {
  return {
    ...media,
    mediaFilters:
      media.mediaFilters?.filter(({ name }) => names.has(name)) ?? null,
  };
}

// Price rows and extras may carry the year's price periods (the next-admin pricing route
// sends them on neither today); the client only reads villaPricing.year (extras) and the
// variant bedrooms/sleeps (prices)
function withoutPricePeriods<T extends object>(item: T): T {
  const { villaPricing } = item as { villaPricing?: object | null };

  if (!villaPricing) return item;

  const { pricePeriods: _pricePeriods, ...rest } = villaPricing as {
    pricePeriods?: unknown;
  };

  return { ...item, villaPricing: rest };
}

function toClientSurrounding(item: VillageSurrounding): VillageSurrounding {
  const { villaSurroundings: _villaSurroundings, ...surrounding } =
    item.surrounding as VillageSurrounding['surrounding'] & {
      villaSurroundings?: unknown;
    };
  const { image, translate, guideSurrounding } = surrounding;

  return {
    ...item,
    surrounding: {
      ...surrounding,
      image: image && {
        id: (image as { id?: number }).id,
        path: image.path,
        filename: image.filename,
      },
      translate: translate.map(({ locale, title, description }) => ({
        locale,
        title,
        description,
      })),
      // Only checked for null (link to the guide page)
      guideSurrounding: guideSurrounding && { id: guideSurrounding.id },
      // Trimmed on purpose: the client reads only these fields
    } as unknown as VillageSurrounding['surrounding'],
  };
}

/**
 * Villa sent to the client components of the villa page: same shape, without the
 * fields and nested copies no client component reads.
 */
export function slimVillageForClient(village: Village): Village {
  return {
    ...village,
    media: village.media.map((item) =>
      withMediaFilters(item, CLIENT_MEDIA_FILTER_NAMES),
    ),
    prices: village.prices?.map((price) => ({
      ...price,
      variant: price.variant && withoutPricePeriods(price.variant),
    })),
    extras: village.extras?.map(withoutPricePeriods),
    surroundings: village.surroundings?.map(toClientSurrounding),
  };
}

const GALLERY_FILTER_NAMES = new Set(['large_gallery_wm_1280_webp']);

/**
 * Villa for cards (listing, carousels, map): cover with all its filters (saved in the
 * shortlist), gallery photos for the gallery modal, main features and visible deals.
 * Fields no card reads are emptied but keep the Village type.
 */
export function toVillageCard(village: Village): Village {
  const cover =
    village.media.find((item) => item.type === 'cover') ?? village.media[0];
  // Keep order and count: the cover (full) plus every gallery photo (slim)
  const media = village.media
    .filter((item) => item === cover || item.type === 'gallery')
    .map((item) =>
      item === cover
        ? item
        : { ...withMediaFilters(item, GALLERY_FILTER_NAMES), translations: [] },
    );

  return {
    ...village,
    media,
    features: village.features?.filter(
      ({ feature }) => feature?.type === 'main_feature',
    ),
    deals: village.deals?.filter(isVillageDealVisible),
    translations: [],
    customRooms: [],
    prices: [],
    priceVariants: [],
    surroundings: [],
    extras: [],
    experiences: [],
    articles: [],
    productReviews: [],
    bookings: [],
    futureBookings: [],
    categories: [],
  };
}

export function withVillageCards<T extends { data: Village[] }>(
  villagesData: T,
): T {
  return { ...villagesData, data: villagesData.data.map(toVillageCard) };
}

export function getVillageDealPromotionTitle(
  deal: VillageDeal,
  lang: string,
): string | undefined {
  const currentLang = ['it', 'fr', 'de', 'en'].includes(lang) ? lang : 'en';

  return deal.deal.translation.find(
    (translation) => translation.locale === currentLang,
  )?.title;
}

// export function villageHasVisiblePromotionNewApi(
//   deals: VillageNewApi['deals'],
// ) {
//   if (deals && deals.length > 0) {
//     const hasPromotion = deals.some((deal) => {
//       const onRange = moment(deal.visibleTo).isSameOrAfter(moment());
//       const isEnabled = deal.enabled && deal.publicVisible;
//       return onRange && isEnabled;
//     });
//     return hasPromotion;
//   }

//   return false;
// }

// export function applyCurrency(value: string, currency?: string) {
//   let formattedValue = parseFloat(value);

//   if (currency == 'usd') {
//     return String(formattedValue * USD_MULTIPLIER);
//   }

//   if (currency == 'gbp') {
//     return String(formattedValue * GBP_MULTIPLIER);
//   }

//   return String(formattedValue);
// }

// export function getVillageWithCurrencyApplied(
//   village: Village,
//   currency?: string,
// ) {
//   const priceVariants = village.priceVariants.map((priceVariant) => {
//     const securityDeposit = applyCurrency(
//       priceVariant.securityDeposit,
//       currency,
//     );

//     const newPriceVariant: VillagePriceVariants = {
//       ...priceVariant,
//       securityDeposit,
//     };
//     return newPriceVariant;
//   });

//   const deals = village.deals.map((deal) => {
//     const value = applyCurrency(deal.deal.value, currency);

//     const newDeal: VillageDeal = {
//       ...deal,
//       deal: {
//         ...deal.deal,
//         value,
//       },
//     };
//     return newDeal;
//   });

//   const extras = village.extras.map((extra) => {
//     const newPrice = applyCurrency(extra.price, currency);

//     const newExtra: VillageExtra = {
//       ...extra,
//       price: newPrice,
//     };

//     return newExtra;
//   });

//   const prices = village.prices.map(({ period, variant, ...price }) => {
//     const securityDeposit = applyCurrency(variant.securityDeposit, currency);

//     const originalPrice = applyCurrency(price.originalPrice, currency);

//     const ownerPrice = applyCurrency(price.ownerPrice, currency);

//     const newPrice: VillagePrice = {
//       period,
//       variant: {
//         ...variant,
//         securityDeposit,
//       },
//       originalPrice,
//       ownerPrice,
//     };
//     return newPrice;
//   });

//   const villageWithCurrencyApplied: Village = {
//     ...village,
//     prices,
//     priceVariants,
//     extras,
//     deals,
//   };

//   return villageWithCurrencyApplied;
// }
